import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { BLOG_POSTS } from "../data/content";
import { FadeUp } from "../components/Reveal";
import { CTASection } from "../components/PageHero";

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);

  if (!post) {
    return (
      <main className="pt-28 sm:pt-32 lg:pt-40 pb-32 text-center" data-testid="post-not-found">
        <h1 className="font-display font-extrabold text-4xl text-aubergine">Article not found</h1>
        <Link to="/blog" className="mt-6 inline-flex items-center gap-2 font-bold text-terracotta">
          <ArrowLeft className="w-4 h-4" /> Back to blog
        </Link>
      </main>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <main data-testid={`blog-post-${post.slug}`}>
      <article className="hospitality-background pt-28 sm:pt-32 lg:pt-40 pb-16 relative overflow-hidden grain-overlay">
        <div className="absolute top-24 -left-32 w-96 h-96 rounded-full bg-saffron-soft blur-3xl opacity-60 drift-c" />
        <div className="max-w-3xl mx-auto px-5 sm:px-8 relative">
          <FadeUp y={12}>
            <Link to="/blog" data-testid="post-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-ink-muted hover:text-terracotta transition-colors">
              <ArrowLeft className="w-4 h-4" /> All articles
            </Link>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="mt-8 flex items-center gap-3 text-xs font-mono uppercase tracking-wider text-terracotta font-semibold flex-wrap">
              <span className="bg-terracotta-soft px-3 py-1.5 rounded-full">{post.category}</span>
              <span className="text-ink-muted font-normal">{post.date}</span>
              <span className="text-ink-muted font-normal flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readMins} min read</span>
            </div>
            <h1 className="mt-5 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl lg:text-5xl text-aubergine">
              {post.title}
            </h1>
            <p className="mt-5 font-serif italic text-xl text-ink-muted leading-relaxed">{post.excerpt}</p>
          </FadeUp>
          <FadeUp delay={0.2} className="mt-10">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_28px_70px_rgba(45,18,77,0.16)] aspect-[16/9]">
              <img src={post.img} alt={post.title} className="w-full h-full object-cover" />
            </div>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-8 sm:mt-10">
            {post.content.map((block, i) => (
              <div key={i} className="mb-9">
                {block.h && (
                  <h2 className="font-display font-bold text-2xl text-aubergine mb-4">{block.h}</h2>
                )}
                {block.p.map((para, j) => (
                  <p key={j} className="text-base sm:text-lg text-ink-muted leading-[1.85] mb-4">{para}</p>
                ))}
              </div>
            ))}
          </FadeUp>
        </div>
      </article>

      <section className="hospitality-background py-12 sm:py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
          <FadeUp className="mb-10">
            <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl text-aubergine">Keep reading</h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p, i) => (
              <FadeUp key={p.slug} delay={i * 0.08}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="hospitality-card group block bg-white rounded-[2rem] border border-line overflow-hidden hover:-translate-y-1.5 hover:shadow-[0_28px_70px_rgba(45,18,77,0.12)] transition-all duration-400"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-mono uppercase tracking-wider text-terracotta font-semibold">{p.category}</span>
                    <h3 className="mt-2 font-display font-bold text-base text-aubergine leading-snug group-hover:text-terracotta transition-colors">{p.title}</h3>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-aubergine">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title="Want us to run this playbook for you?"
        sub="Book a free strategy call — we'll map these ideas to your venue, your city and your numbers."
      />
    </main>
  );
}
